import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Ban } from "lucide-react";
import { toast } from "sonner";

const STATE = {
    confirmada: "bg-success text-white",
    cumplida: "bg-primary text-primary-foreground",
    no_se_presento: "bg-destructive text-destructive-foreground",
    cancelada: "bg-muted text-muted-foreground",
};

export default function ReservasDelDia() {
    const [items, setItems] = useState([]);
    const [salas, setSalas] = useState([]);
    const [unidades, setUnidades] = useState([]);
    const today = new Date().toISOString().slice(0, 10);

    const load = () => api.get("/reservas", { params: { fecha_desde: today, fecha_hasta: today } }).then((r) => setItems(r.data));
    useEffect(() => {
        api.get("/salas").then((r) => setSalas(r.data));
        api.get("/unidades").then((r) => setUnidades(r.data));
        load();
    }, []); // eslint-disable-line

    const salaName = (id) => salas.find((s) => s.sala_id === id)?.nombre || "—";
    const uniName = (id) => unidades.find((u) => u.unidad_id === id)?.etiqueta || "—";

    const marcar = async (r, estado) => {
        try {
            await api.post(`/reservas/${r.reserva_id}/marcar`, null, { params: { estado } });
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const cancelar = async (r) => {
        if (!window.confirm(`¿Cancelar la reserva de ${salaName(r.sala_id)} (${r.hora_inicio}–${r.hora_fin})?`)) return;
        try {
            await api.post(`/reservas/${r.reserva_id}/cancelar`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Orden: confirmadas primero por hora, luego el resto
    const sorted = [...items].sort((a, b) => {
        const pa = a.estado === "confirmada" ? 0 : 1;
        const pb = b.estado === "confirmada" ? 0 : 1;
        if (pa !== pb) return pa - pb;
        return a.hora_inicio.localeCompare(b.hora_inicio);
    });

    return (
        <div>
            <PageHeader title="Reservas del día" subtitle={today} />
            {sorted.length === 0 ? <EmptyState title="Sin reservas para hoy" /> :
                <div className="grid gap-2">
                    {sorted.map((r) => (
                        <Card key={r.reserva_id}>
                            <CardContent className="p-3 flex flex-col md:flex-row md:items-center gap-3">
                                <div className="font-mono text-base text-primary md:w-24 whitespace-nowrap">{r.hora_inicio}–{r.hora_fin}</div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-heading font-semibold truncate">{salaName(r.sala_id)} <span className="text-muted-foreground text-sm font-normal">· {r.unidad_etiqueta || uniName(r.unidad_id)}</span></p>
                                    <p className="text-xs text-muted-foreground truncate">
                                        Creada por: {r.creado_por_nombre || <span className="italic">No identificado</span>}
                                        {r.creado_por_rol && r.creado_por_rol !== "usuario_edificio" && (
                                            <span className="capitalize"> ({r.creado_por_rol})</span>
                                        )}
                                    </p>
                                    {r.motivo && <p className="text-xs text-muted-foreground truncate">{r.motivo}</p>}
                                </div>
                                <Badge className={STATE[r.estado] || ""}>{r.estado.replace("_", " ")}</Badge>
                                {r.estado === "confirmada" && (
                                    <div className="flex gap-1 flex-wrap">
                                        <Button size="sm" variant="outline" onClick={() => marcar(r, "cumplida")} data-testid={`marcar-cumplida-${r.reserva_id}`}>
                                            <CheckCircle2 className="h-4 w-4 mr-1" />Cumplida
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => marcar(r, "no_se_presento")}>
                                            <XCircle className="h-4 w-4 mr-1" />No se presentó
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                            onClick={() => cancelar(r)}
                                            data-testid={`cancelar-${r.reserva_id}`}
                                        >
                                            <Ban className="h-4 w-4 mr-1" />Cancelar
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            }
        </div>
    );
}
